import { MenuItem, Select } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";

import HeaderBread from "../../../components/HeaderBread";
import { useDataLayerValue } from "../../../DataLayer/DataLayer";
import Layout from "../../../Layout";
import CustomHeaderTitle from "../../../components/constants/CustomHeaderTitle";
import SwitchBar from "../../../components/Pages/SwitchBar";
import SearchField from "../../../components/SearchField";
import CustomizedTable from "../../../components/UI/CustomizedTable";
import Vessel from "../../../components/UI/VesselFilter";
import { getAncillaryErrors } from "../../../utils/ApiCalls/MobileAncillary";
import { getVessels } from "../../../utils/ApiCalls/SpecialPage/index";

const { RangePicker } = DatePicker;

const statusChipStyle = (status) => {
  const base = {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "10px",
    fontSize: "11px",
    fontWeight: 500,
    color: "#fff",
  };
  switch ((status || "").toUpperCase()) {
    case "CONTEXT_FAILED":
      return { ...base, background: "#eb6363" };
    case "ENROLLMENT_FAILED":
      return { ...base, background: "#d32f2f" };
    case "FAILED":
      return { ...base, background: "#b71c1c" };
    case "SUCCESS":
      return { ...base, background: "#0ab402" };
    case "AUTHORISING":
      return { ...base, background: "#1976d2" };
    default:
      return { ...base, background: "#9e9e9e" };
  }
};

const isStuckChipStyle = (isStuck) => ({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "10px",
  fontSize: "11px",
  fontWeight: 500,
  color: "#fff",
  background: isStuck ? "#f5a623" : "#9e9e9e",
});

const AncillaryErrorModule = () => {
  const navigate = useNavigate();
  const [, dispatch] = useDataLayerValue();

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Error Module", route: "/shore/mobile-ancillary/error-module" },
  ];

  const [active, setActive] = useState("listing");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [vessel, setVessel] = useState([]);
  const [vesselId, setVesselId] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [createdAt, setCreatedAt] = useState(
    JSON.stringify([
      moment().subtract(3, "months").format("YYYY-MM-DD"),
      moment().format("YYYY-MM-DD"),
    ])
  );

  const columns = [
    {
      title: "Sl",
      dataIndex: "id",
      key: "id",
      width: 40,
      render: (val, rec, idx) => (page - 1) * pageSize + idx + 1,
    },
    {
      title: "Booking ID",
      dataIndex: "bookingId",
      key: "bookingId",
      width: 120,
    },
    {
      title: "Vessel",
      dataIndex: "vessel",
      key: "vessel",
      width: 80,
      render: (val) => val || "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 110,
      render: (val, rec) =>
        val != null ? `${rec.currency || ""} ${Number(val).toFixed(2)}` : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (val) => <span style={statusChipStyle(val)}>{val || "-"}</span>,
    },
    {
      title: "Failed Stage",
      dataIndex: "failedStage",
      key: "failedStage",
      width: 130,
      render: (val) => val || "-",
    },
    {
      title: "Error Message",
      dataIndex: "errorMessage",
      key: "errorMessage",
      width: 200,
      render: (val) =>
        val ? (
          <span title={val}>
            {val.length > 60 ? val.slice(0, 60) + "..." : val}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Is Stuck</span>,
      dataIndex: "isStuck",
      key: "isStuck",
      width: 80,
      render: (val) => (
        <span style={isStuckChipStyle(val)}>{val ? "Yes" : "No"}</span>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (val) => (val ? moment(val).format("DD-MM-YYYY HH:mm") : "-"),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (text, record) => (
        <div className="table_action_btn">
          <button
            className="btn delete_btn"
            title="View"
            onClick={() =>
              navigate("/shore/mobile-ancillary/error-module/view/" + record.id)
            }
          >
            <i className="ri-eye-line"></i>
          </button>
        </div>
      ),
    },
  ];

  const getVesselData = async () => {
    dispatch({ type: "SET_LOADING", loading: true });
    let vessels = await getVessels();
    if (vessels.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: vessels.error, type: "error" },
      });
    } else {
      vessels.push({ id: null, name: "All" });
      setVessel(vessels);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  const getData = async (value) => {
    dispatch({ type: "SET_LOADING", loading: true });
    const args = {
      search,
      page: value || page,
      limit: pageSize,
      created_at: createdAt,
    };
    if (status) args.status = status;
    if (vesselId && vessel.length) {
      const selectedVessel = vessel.find((v) => v.id === vesselId);
      if (selectedVessel) args.vessel = selectedVessel.name;
    }
    let data = await getAncillaryErrors(args);
    if (data.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
    } else {
      setRows(data.data || []);
      setCount(data.count || 0);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  const sortHandler = async () => {
    await getData(page);
  };

  useEffect(() => {
    getVesselData();
  }, []);

  useEffect(() => {
    if (active === "listing") {
      getData(page);
    }
  }, [page, pageSize, search, status, vesselId, createdAt, active]);

  return (
    <Layout type={"read_per"} module_data={"Error Module"}>
      <CustomHeaderTitle title={"Error Module"} />
      <div>
        <HeaderBread navigation={navigation} title={"Error Module"} />
        <section className="content" style={{ marginTop: "20px" }}>
          <div className="container-fluid">
            <SwitchBar
              tabs={[
                { title: "Listing", value: "listing" },
              ]}
              activeTab={active}
              setActiveTab={(item) => {
                setCreatedAt(
                  JSON.stringify([
                    moment().subtract(3, "months").format("YYYY-MM-DD"),
                    moment().format("YYYY-MM-DD"),
                  ])
                );
                setActive(item);
              }}
            />
            {active === "listing" && (
              <div className="card">
                <div className="card-header two_item" style={{ alignItems: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      flexWrap: "wrap",
                    }}
                  >
                    <Select
                      style={{ width: "180px", height: "40px" }}
                      displayEmpty
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.value);
                        setPage(1);
                      }}
                      renderValue={status !== "" ? undefined : () => "All Status"}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="CONTEXT_FAILED">Context Failed</MenuItem>
                      <MenuItem value="ENROLLMENT_FAILED">Enrollment Failed</MenuItem>
                      <MenuItem value="FAILED">Failed</MenuItem>
                      <MenuItem value="AUTHORISING">Stuck (Authorising)</MenuItem>
                    </Select>
                    <Vessel
                      setVesselId={(id) => {
                        setVesselId(id);
                        setPage(1);
                      }}
                      vesselData={vessel}
                      vesselId={vesselId}
                      getData={getData}
                      setPage={setPage}
                      width={150}
                    />
                    <RangePicker
                      style={{ width: "250px" }}
                      onChange={(date, dateString) => {
                        setCreatedAt(JSON.stringify(dateString));
                        setPage(1);
                      }}
                      defaultValue={[
                        dayjs(
                          dayjs().subtract(3, "month").format("YYYY-MM-DD"),
                          "YYYY-MM-DD"
                        ),
                        dayjs(dayjs().format("YYYY-MM-DD"), "YYYY-MM-DD"),
                      ]}
                      format={"YYYY-MM-DD"}
                      disabledDate={(current) => moment() <= current}
                    />
                  </div>
                  <SearchField
                    value={search}
                    SearchValue={() => setPage(1)}
                    setValue={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="card-body">
                  <div className="table_holder">
                    <CustomizedTable
                      setRows={setRows}
                      setPageSize={setPageSize}
                      setPage={setPage}
                      pageSize={pageSize}
                      page={page}
                      count={count}
                      columns={columns}
                      rows={rows}
                      sortHandler={sortHandler}
                      title={"Error Module"}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default AncillaryErrorModule;
